import { ReactNode } from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import { ArrowMoveDownRightIcon } from "@hugeicons-pro/core-stroke-rounded";
import { Question } from "types/landlord/verificationTemplate";
import { cx } from "shared/utils";
import { AssetItem } from "../../components/SmartRender/AssetItem";
import { DynamicValue } from "../../components/SmartRender/DynamicValue";
import { sortAndFilterObjectValues } from "../../components/SmartRender/ObjectItem";

type Asset = { url: string; content_type: string; filename?: string };

const getAsset = (value: unknown): Asset | undefined => {
  const asset = (value as { asset?: Partial<Asset> } | null)?.asset;
  return asset?.url && asset.content_type ? (asset as Asset) : undefined;
};

// A file answer reads as a link with its name, like the design. Printing
// still gets the file itself, as before: images inline, PDFs at the end.
const Answer = ({ name, value }: { name: string; value: unknown }) => {
  if (value == null || value === "") return "-";

  const asset = getAsset(value);
  if (!asset) return <DynamicValue detailsKey={name} value={value} />;

  return (
    <>
      <a
        href={asset.url}
        target="_blank"
        rel="noreferrer"
        className="text-primary-500 underline print:hidden"
      >
        {asset.filename || "Document"}
      </a>
      <div className="hidden print:block">
        <AssetItem
          url={asset.url}
          content_type={asset.content_type}
          assetName={asset.filename || name}
        />
      </div>
    </>
  );
};

// Same look as the report's Row, with block-level cells so a printed file
// preview can sit in the value column.
const AnswerRow = ({
  title,
  children,
  divider
}: {
  title: ReactNode;
  children: ReactNode;
  divider: boolean;
}) => (
  <div
    className={cx(
      "flex gap-2 py-3.5 text-sm font-medium",
      divider ? "border-b border-alpha-40" : ""
    )}
  >
    <div className="w-[200px] shrink-0 break-words text-content-300 print:text-content-800">
      {title}
    </div>
    <div className="min-w-0 flex-1 break-words text-content-1000">
      {children}
    </div>
  </div>
);

// Answers in builder order; a conditional question's answer sits under its
// parent's, marked with an arrow and indented inside the label column so all
// answers stay in one column. Questions the applicant never saw have no
// answer (the API drops them), so they don't show up here.
export const AdditionalQuestionsAnswers = ({
  answers,
  questions
}: {
  answers: Record<string, unknown>;
  questions: Question[];
}) => {
  const ids = new Set(questions.map((question) => question.id));
  const conditionalNames = new Set(
    questions
      .filter(
        ({ parent_question_id }) =>
          parent_question_id && ids.has(parent_question_id)
      )
      .map((question) => question.name)
  );
  const entries = sortAndFilterObjectValues(Object.entries(answers), {
    order: questions.map((question) => question.name)
  });

  return (
    <div className="mt-2">
      {entries.map(([name, value], index) => (
        <AnswerRow
          key={name}
          divider={index < entries.length - 1}
          title={
            conditionalNames.has(name) ? (
              <span className="flex gap-1.5 pl-5">
                <HugeiconsIcon
                  icon={ArrowMoveDownRightIcon}
                  size={16}
                  className="mt-0.5 shrink-0 text-content-100"
                />
                {name}
              </span>
            ) : (
              name
            )
          }
        >
          <Answer name={name} value={value} />
        </AnswerRow>
      ))}
    </div>
  );
};
